import React, { useState } from 'react';
import classnames from 'classnames';
import styles from './CollapsibleSection.module.scss';
import { ReactComponent as ArrowDownIcon } from 'app/assets/Icons/ArrowDown.svg';
import { OptionsButton } from '../OptionsButton/OptionsButton';
import { InlineEditor } from '../InlineEditor/InlineEditor';
import { useAppSelector, getSelectedDocuments } from 'app/redux';
import { LoadingSpinner } from 'app/components';
import { ICollapsibleSection } from './CollapsibleSection.interface';

export const CollapsibleSection: React.FC<ICollapsibleSection> = ({
  title,
  icon,
  children,
  showOptionsButton = false,
  onCreateFolder,
  onDeleteDocument,
  onUploadDocument,
  isLoading = false,
  onCollapseFolders
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const selectedDocuments = useAppSelector(getSelectedDocuments);
  const hasSelectedDocuments = selectedDocuments.length > 0;

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };

  const handleCreateFolder = (): void => {
    setIsCreatingFolder(true);
  };

  const handleSaveFolder = (folderName: string): void => {
    if (onCreateFolder) {
      onCreateFolder(folderName);
    }
    setIsCreatingFolder(false);
  };

  const handleCancelFolder = (): void => {
    setIsCreatingFolder(false);
  };

  const getOptionsVariant = (): 'section' | 'section-with-selection' => {
    return hasSelectedDocuments ? 'section-with-selection' : 'section';
  };

  return (
    <div className={styles.container}>
      <button
        className={styles.header}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
      >
        <div className={styles['header-content']}>
          <ArrowDownIcon
            className={classnames(styles['toggle-icon'], {
              [styles.expanded]: isExpanded
            })}
          />
          <span className={styles.title}>{title}</span>
          <div className={styles.icon}>{icon}</div>
        </div>

        <div className={styles['header-actions']}>
          {isLoading && (
            <LoadingSpinner
              containerStyle={{
                position: 'relative',
                width: '1.5rem',
                height: '1.5rem',
                backdropFilter: 'unset'
              }}
              imageStyle={{ width: '1.5rem', height: '1.5rem' }}
            />
          )}
          {showOptionsButton && (
            <OptionsButton
              variant={getOptionsVariant()}
              onCreateFolder={handleCreateFolder}
              onDelete={onDeleteDocument}
              onUpload={onUploadDocument}
              onCollapse={onCollapseFolders}
            />
          )}
        </div>
      </button>

      <div
        className={classnames(styles.content, {
          [styles.expanded]: isExpanded
        })}
      >
        <div className={styles.inner}>
          {isCreatingFolder && (
            <div className={styles['create-folder']}>
              <InlineEditor
                value=""
                onSave={handleSaveFolder}
                onCancel={handleCancelFolder}
                placeholder="New folder name..."
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
